import { BaseService } from '@services/BaseService';
import { OptionsViewModel, AddressViewModel, PageViewModel } from '@models/common.models';
import { CommentViewModel } from '@models/Comment';
import {
  ContractCommentTypesViewModel,
  ContractDetailsViewModel,
  ContractDuplicateAddressReviewModel,
  ContractResaleEmailViewModel,
  ContractResaleModalViewModel,
  ContractSimpleViewModel,
  ContractViewModel,
  PropertyTypeViewModel,
  SendPaymentReceiptEmailRequest,
  EmailNotificationViewModel,
  ContractSendEmailsRequest,
  ContractBalancesViewModel
} from '@models/Contract';
import { PaymentViewModel, ContractPaymentDetailViewModel, ContractCheckPaymentViewModel } from '@models/Payment';
import {
  ContractCancellationModel,
  ContractCancellationNotificationModel,
  ContractPaymentInfoModel,
  CancelledContractModel,
  ContractOrderCancellationModel
} from '@models/ContractCancellation';
import {
  ContractTotalCostHistoryViewModel,
  ContractSearchViewModel,
  CviOptionsViewModel,
  CVIExclusionViewModel
} from '@models/ServiceWorkOrder';
import { ContractExclusionViewModel, ContractCviViewModel } from '@models/ServiceWorkOrder';
import { ContractCviCostHistoryViewModel } from '@models/ContractCviCostHistoryViewModel';
import { ContractTermSearchViewModel } from '@models/ContractHistory';
import { SimpleAccountViewModel } from '@models/SimpleAccountViewModel';
import { dateParser } from '@common/dateParser';
import { CustomerContractModel } from '@models/Customer';
import { ContractErrorViewModel } from '@models/ContractError';

class ContractService extends BaseService {
  constructor() {
    super('contract');
  }

  activateContract(id: number, coeDate?: Date) {
    const model = coeDate ? { coeDate: coeDate } : {};
    return this.fetch(`contracts/${id}/activate`, {
      body: JSON.stringify(model),
      method: 'POST'
    });
  }

  saveContract(model: ContractViewModel) {
    return this.fetch<ContractViewModel>('contracts', {
      method: 'POST',
      body: JSON.stringify(model)
    });
  }

  saveResaleContract(model: ContractViewModel, resaleId: number) {
    return this.fetch<number>(`${resaleId}/resale`, {
      method: 'POST',
      body: JSON.stringify(model)
    });
  }

  processPayment(contractId: number, payment: PaymentViewModel) {
    return this.fetch(`contracts/${contractId}/processPayment`, {
      body: JSON.stringify(payment),
      method: 'POST'
    });
  }

  finalizeContract(contractId: number, paymentAmount: number, demandLetter: boolean) {
    let request = { paymentAmount: paymentAmount, demandLetter: demandLetter };

    return this.fetch(`contracts/${contractId}/finalize`, {
      body: JSON.stringify(request),
      method: 'POST'
    });
  }

  getContractDetails(id: number, term?: number) {
    const termString = typeof term !== 'undefined' ? `?term=${term}` : '';

    return this.fetch<ContractDetailsViewModel>(`contracts/${id}${termString}`, {
      method: 'GET'
    });
  }

  getContactTypes() {
    return this.fetch<OptionsViewModel[]>('ContactTypes', {
      method: 'GET'
    });
  }

  getContactMedium() {
    return this.fetch<OptionsViewModel[]>('ContactMedium', {
      method: 'GET'
    });
  }

  getContactInitiator() {
    return this.fetch<OptionsViewModel[]>('ContactInitiator', {
      method: 'GET'
    });
  }

  getPropertyType() {
    return this.fetch<PropertyTypeViewModel[]>('PropertyType', {
      method: 'GET'
    });
  }

  getSaleType() {
    return this.fetch<OptionsViewModel[]>('SaleType', {
      method: 'GET'
    });
  }

  getBillingOptions() {
    return this.fetch<OptionsViewModel[]>('BillingOptions', {
      method: 'GET'
    });
  }

  getAssetManagers() {
    return this.fetch<OptionsViewModel[]>('AssetManagers', {
      method: 'GET'
    });
  }

  getContractsByRealEstateAgentId(id?: number, pageNumber?: number, pageSize?: number) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    return this.fetch<PageViewModel<ContractSimpleViewModel>>('ContractByRealEstateAgent', {
      method: 'POST',
      body: JSON.stringify({
        id: id,
        pageNumber: pn,
        pageSize: ps
      })
    });
  }

  getContractsByPropertyManagerId(id?: number, pageNumber?: number, pageSize?: number) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;
    return this.fetch<PageViewModel<ContractSimpleViewModel>>('ContractByPropertyManager', {
      method: 'POST',
      body: JSON.stringify({
        id: id,
        page: pn,
        pageSize: ps
      })
    });
  }

  searchByCustomer(customerId: number, pageNumber?: number, pageSize?: number) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    return this.fetch<PageViewModel<ContractSimpleViewModel>>('ContractByCustomer', {
      method: 'POST',
      body: JSON.stringify({ id: customerId, pageNumber: pn, pageSize: ps })
    });
  }

  searchRelatedByCustomer(customerId: number, pageNumber?: number, pageSize?: number) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    return this.fetch<PageViewModel<ContractSimpleViewModel>>('RelatedContractByCustomer', {
      method: 'POST',
      body: JSON.stringify({ id: customerId, pageNumber: pn, pageSize: ps })
    });
  }

  searchRelatedByContract(contractId: number, pageNumber?: number, pageSize?: number) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    return this.fetch<PageViewModel<ContractSimpleViewModel>>('RelatedContractByContract', {
      method: 'POST',
      body: JSON.stringify({ id: contractId, pageNumber: pn, pageSize: ps })
    });
  }

  searchByEscrowOfficer(escrowOfficerId: number, pageNumber?: number, pageSize?: number) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    return this.fetch<PageViewModel<ContractSimpleViewModel>>('ContractByEscrowOfficer', {
      method: 'POST',
      body: JSON.stringify({
        id: escrowOfficerId,
        pageNumber: pn,
        pageSize: ps
      })
    });
  }

  searchByTransactionCoordinator(transactionCoordinatorId: number, pageNumber?: number, pageSize?: number) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    return this.fetch<PageViewModel<ContractSimpleViewModel>>('ContractByTxCoordinator', {
      method: 'POST',
      body: JSON.stringify({
        id: transactionCoordinatorId,
        pageNumber: pn,
        pageSize: ps
      })
    });
  }

  checkDuplicateAddresses(address: AddressViewModel) {
    return this.fetch<ContractDuplicateAddressReviewModel[]>('CheckDuplicateAddress', {
      method: 'POST',
      body: JSON.stringify(address)
    });
  }

  searchContractComments(
    query: string | null,
    contractId?: number,
    pageNumber?: number,
    pageSize?: number,
    filterBy?: string
  ) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    return this.fetch<PageViewModel<CommentViewModel>>('ContractCommentsSearch', {
      method: 'POST',
      body: JSON.stringify({
        id: contractId,
        query: query,
        page: pn,
        pageSize: ps,
        filterBy: filterBy
      })
    });
  }

  getContractCommentTypes() {
    return this.fetch<ContractCommentTypesViewModel[]>('ContractCommentTypes', {
      method: 'GET'
    });
  }

  cancelContractOrder(id: number, orderCancellationModel: ContractOrderCancellationModel) {
    return this.fetch(`contracts/${id}/cancelOrder`, {
      method: 'POST',
      body: JSON.stringify(orderCancellationModel)
    });
  }

  getContractCancellationNotifications(id: number): Promise<Array<ContractCancellationNotificationModel>> {
    return this.fetch(`contracts/${id}/emailNotifications`, {
      method: 'GET'
    });
  }

  getContractCancellationReasons(id: number): Promise<Array<OptionsViewModel>> {
    return this.fetch(`contracts/${id}/cancellationReason`, {
      method: 'GET'
    });
  }

  calculateCancellationRefund(id: number, calcParams: { cancellationDate: Date }): Promise<ContractCancellationModel> {
    return this.fetch(
      `contracts/${id}/calculateRefund?cancellationDate=${dateParser
        .toDateString(calcParams.cancellationDate)
        .replace(/(")*/gi, '')}`,
      {
        method: 'GET'
      }
    );
  }

  cancelContract(id: number, cancellationModel: ContractCancellationModel) {
    return this.fetch<ContractCancellationModel>(`contracts/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify(cancellationModel)
    });
  }

  getContract(id: number): Promise<ContractViewModel> {
    return this.fetch<ContractViewModel>(`contracts/${id}/simpleView`, {
      method: 'GET'
    });
  }

  getResaleContract(contractId: number): Promise<ContractViewModel> {
    return this.fetch<ContractViewModel>(`${contractId}/resale/contract`, {
      method: 'GET'
    });
  }

  updateContract(id: number, contract: ContractViewModel) {
    const contractString = JSON.stringify(contract);
    return this.fetch(`contracts/${id}`, {
      method: 'PUT',
      body: contractString
    });
  }

  updateCoe(id: number, coe: Date) {
    return this.fetch(`contracts/${id}/coe`, {
      method: 'PUT',
      body: JSON.stringify(coe)
    });
  }

  getContractResaleData(id: number, date?: Date, isEdit?: boolean) {
    if (!date) {
      date = new Date(Date.now());
    }
    var dto = new Date().getTimezoneOffset();
    date = new Date(date.getTime() - dto * 60000);
    const editString = isEdit !== undefined ? `?isEdit=` + isEdit : '';
    return this.fetch<ContractResaleModalViewModel>(`${id}/resale/credit/${editString}`, {
      method: 'POST',
      body: JSON.stringify(date)
    });
  }

  getRenewalContract(id: number) {
    return this.fetch<ContractViewModel>(`contracts/${id}/renew`, {
      method: 'GET'
    });
  }

  getContractResaleEmailNotifications(id: number) {
    return this.fetch<Array<ContractResaleEmailViewModel>>(`contracts/${id}/emailnotifications`, {
      method: 'GET'
    });
  }

  getRenewalChangeFee(contractId: number, propertyTypeId: number, productId: number) {
    return this.fetch<number>(
      `contracts/${contractId}/renewalChangeFee?propertyTypeId=${propertyTypeId}&productId=${productId}`,
      {
        method: 'GET'
      }
    );
  }

  sendResaleEmails(id: number, emails: Array<string>) {
    var json = JSON.stringify(emails);
    return this.fetch(`${id}/resale/notifications`, {
      method: 'POST',
      body: json
    });
  }

  sendRenewalNotice(id: number) {
    return this.fetch(`contracts/${id}/renewalNotice`, {
      method: 'GET'
    });
  }

  getCostHistory(id: number) {
    return this.fetch<ContractTotalCostHistoryViewModel[]>(`${id}/costhistory`, {
      method: 'GET'
    });
  }

  getExclusionList(id: number) {
    return this.fetch<Array<ContractExclusionViewModel>>(`${id}/exclusions`, {
      method: 'GET'
    });
  }

  saveExclusion(exclusionEntry: CVIExclusionViewModel) {
    if (exclusionEntry.id) {
      return this.fetch(`exclusion/${exclusionEntry.id}`, {
        method: 'PUT',
        body: JSON.stringify(exclusionEntry)
      });
    }
    return this.fetch(`exclusion`, {
      method: 'POST',
      body: JSON.stringify(exclusionEntry)
    });
  }

  getContractCVI(contractId: number) {
    return this.fetch<ContractCviViewModel[]>(`${contractId}/cvis`, {
      method: 'GET'
    });
  }

  searchCVIs(
    contractId: number,
    query: string | null,
    id: number | null,
    pageSize?: number,
    pageNumber?: number,
    orderBy?: string,
    orderDirection?: string
  ) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    const body = {
      query: query,
      pageNumber: pn,
      pageSize: ps,
      id: id,
      orderBy: orderBy,
      orderDirection: orderDirection
    };

    return this.fetch<PageViewModel<CviOptionsViewModel>>(`${contractId}/cviSearch`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }
  saveCVI(CVI: ContractCviViewModel) {
    if (CVI.id) {
      return this.fetch(`cvi/${CVI.id}`, {
        method: 'PUT',
        body: JSON.stringify(CVI)
      }) as Promise<ContractCviViewModel>;
    }

    return this.fetch(`cvi`, {
      method: 'POST',
      body: JSON.stringify(CVI)
    }) as Promise<ContractCviViewModel>;
  }

  sendPaymentReceiptEmail(id: number, emailAddresses: Array<string>, paymentDate: Date, creditCardLastFour?: string) {
    var requestObject: SendPaymentReceiptEmailRequest = {
      creditCardLastFour: creditCardLastFour,
      emailAddresses: emailAddresses,
      paymentDate: paymentDate
    };
    return this.fetch(`contracts/${id}/sendPaymentReceiptEmail`, {
      method: 'POST',
      body: JSON.stringify(requestObject)
    });
  }

  getContractCviCostHistory(contractCviId: number) {
    return this.fetch<ContractCviCostHistoryViewModel>(`cvi/${contractCviId}/costHistory`, {
      method: 'GET'
    });
  }

  searchCancelledContractsList(
    query: string | null,
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    orderDirection: string,
    filterByDisbursed: boolean
  ) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;
    if (query) {
      query = query.trim();
    }
    return this.fetch<PageViewModel<CancelledContractModel>>('cancelledSearch', {
      method: 'POST',
      body: JSON.stringify({
        id: 0,
        query: query,
        pageNumber: pn,
        pageSize: ps,
        orderBy: orderBy,
        orderDirection: orderDirection,
        filterBy: filterByDisbursed
      })
    });
  }

  changeAccountingOwnership(contractId: number, release?: boolean): Promise<CancelledContractModel> {
    if (release) {
      return this.fetch(`${contractId}/release`, {
        method: 'POST'
      });
    } else {
      return this.fetch(`${contractId}/own`, {
        method: 'POST'
      });
    }
  }

  getCancelledContracts() {
    return this.fetch<Array<CancelledContractModel>>(`cancelledContracts`, {
      method: 'GET'
    });
  }

  getPaymentInfo(contractId: number) {
    return this.fetch<ContractPaymentInfoModel>(`${contractId}/paymentInfo`, {
      method: 'GET'
    });
  }

  getBillableEntities(contractId: number) {
    return this.fetch<SimpleAccountViewModel[]>(`contracts/${contractId}/billableEntities`, {
      method: 'GET'
    });
  }

  getNoRenewReasons() {
    return this.fetch<OptionsViewModel[]>(`NoRenewalReasons`, {
      method: 'GET'
    });
  }

  suspendContract(contractId: number) {
    return this.fetch(`${contractId}/suspend`, {
      method: 'POST'
    });
  }

  resumeContract(contractId: number) {
    return this.fetch(`${contractId}/resume`, {
      method: 'POST'
    });
  }

  verify(contractId: number) {
    return this.fetch(`${contractId}/verify`, {
      method: 'POST'
    });
  }

  setUSPSStatus(contractId: number, uspsStatus: number) {
    return this.fetch(`${contractId}/setUSPSStatus`, {
      method: 'POST',
      body: JSON.stringify(uspsStatus)
    });
  }

  getContractMetadata(contractId: number) {
    return this.fetch(`${contractId}/metadata`, {
      method: 'GET'
    });
  }

  public searchContractTerms(
    pageNumber: number,
    pageSize: number,
    orderBy: string,
    orderByDirection: string,
    query?: string,
    contractId?: number
  ): Promise<PageViewModel<ContractTermSearchViewModel>> {
    let queryParameters = [];

    // filters
    if (contractId != null) {
      queryParameters.push(`contractId=${contractId}`);
    }

    if (query) {
      queryParameters.push(`query=${query}`);
    }

    // paging
    queryParameters.push(`pageNumber=${Math.max(1, pageNumber)}`);
    queryParameters.push(`pageSize=${Math.max(0, pageSize)}`);
    // sorting
    if (orderBy) {
      queryParameters.push(`orderBy=${encodeURIComponent(orderBy)}`);
    }
    if (orderByDirection) {
      queryParameters.push(`orderByDirection=${encodeURIComponent(orderByDirection)}`);
    }

    const uri: string = 'terms'.concat(queryParameters.length > 0 ? '?' : '', queryParameters.join('&'));

    return this.fetch<PageViewModel<ContractTermSearchViewModel>>(uri, {
      method: 'GET'
    });
  }

  getEmailList(contractId: number) {
    return this.fetch<EmailNotificationViewModel[]>(`contracts/${contractId}/emailNotifications`, {
      method: 'GET'
    });
  }

  sendConfirmation(contractId: number, sendEmailsRequest: ContractSendEmailsRequest) {
    const request = JSON.stringify(sendEmailsRequest);
    return this.fetch(`${contractId}/emails/confirmation`, {
      method: 'POST',
      body: request
    });
  }

  getPaymentDetail(contractId: number) {
    return this.fetch<ContractPaymentDetailViewModel>(`contracts/${contractId}/paymentDetail`, {
      method: 'GET'
    });
  }

  creditCardToDemandLetter(contractId: number) {
    return this.fetch(`contracts/${contractId}/creditCardToDemandLetter`, {
      method: 'POST'
    });
  }

  getContractBalances(contractId: number) {
    return this.fetch<ContractBalancesViewModel[]>(`contracts/${contractId}/balances`, {
      method: 'GET'
    });
  }

  searchContracts = (
    query: string | null,
    id?: number,
    pageNumber?: number,
    pageSize?: number,
    orderBy?: string,
    orderDirection?: string
  ) => {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    return this.fetch<PageViewModel<ContractSearchViewModel>>('search', {
      method: 'POST',
      body: JSON.stringify({
        id: id,
        query: query,
        pageNumber: pn,
        pageSize: ps,
        orderBy: orderBy,
        orderDirection: orderDirection
      })
    });
  };

  getPaymentSources() {
    return this.fetch<OptionsViewModel[]>(`paymentSources`, {
      method: 'GET'
    });
  }

  saveCheckPayment(contractCheckPayment: ContractCheckPaymentViewModel) {
    const request = JSON.stringify(contractCheckPayment);
    return this.fetch(`contracts/${contractCheckPayment.contractId}/checkPayment`, {
      method: 'POST',
      body: request
    });
  }

  saveRelatedContract(customerContract: CustomerContractModel) {
    const request = JSON.stringify(customerContract);
    return this.fetch(`relateContract`, {
      method: 'POST',
      body: request
    });
  }

  removeRelatedContract(customerContract: CustomerContractModel) {
    const request = JSON.stringify(customerContract);
    return this.fetch(`unrelateContract`, {
      method: 'POST',
      body: request
    });
  }

  contractErrorSearch = (pageNumber?: number, pageSize?: number, orderBy?: string, orderDirection?: string) => {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    return this.fetch<PageViewModel<ContractErrorViewModel>>('contractError/search', {
      method: 'POST',
      body: JSON.stringify({
        query: '',
        pageNumber: pn,
        pageSize: ps,
        orderBy: orderBy,
        orderDirection: orderDirection
      })
    });
  };

  contractErrorComplete = (contractErrorId: number, completeDate: Date) => {
    const requestBody = JSON.stringify(completeDate);
    return this.fetch(`contractError/${contractErrorId}/complete`, {
      method: 'POST',
      body: requestBody
    });
  };
}

export const contractService = new ContractService();



// WEBPACK FOOTER //
// ./src/_services/ContractService.ts